import { useCallback, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FilterPanelCheckboxGroup, {
  type FilterPanelCheckItem,
} from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LoyaltyPointsSliderSearch from 'loyalty/components/LoyaltyPointsSliderSearch'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useSearchBurnEnabled from 'hooks/useSearchBurnEnabled'

const SliderContainer = styled.div`
  margin-top: ${rem(4)};
  margin-bottom: ${rem(16)};
  min-width: 0;

  &:empty {
    display: none;
    margin-bottom: 0;
  }
`

const FilterContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};

  > * {
    margin-top: 0;
  }
`

interface Props {
  cacheKey?: string
  skipBatchMaxCap?: boolean
  skipBurnExperiment?: boolean
  viewPriceInPoints: boolean
  onViewPriceInPointsChange: (viewPriceInPoints: boolean) => void
  compact?: boolean
  showPointsFilters?: boolean
  title?: ReactNode
  additionalFilters?: ReactNode
  loading?: boolean
}

function LoyaltySocietePointsFilterGroup({
  cacheKey,
  skipBatchMaxCap,
  skipBurnExperiment,
  viewPriceInPoints,
  onViewPriceInPointsChange,
  compact,
  showPointsFilters = true,
  title,
  additionalFilters,
  loading,
}: Props) {
  const intl = useIntl()
  const { setSearchBurnEnabled, setPointsToBurn } = useSearchBurnEnabled()

  const clearViewPriceInPoints = useCallback(() => {
    onViewPriceInPointsChange(false)
  }, [onViewPriceInPointsChange])

  const handleViewPriceInPointsToggle = useCallback(() => {
    if (viewPriceInPoints) {
      clearViewPriceInPoints()
    } else {
      setSearchBurnEnabled(false)
      setPointsToBurn(undefined)
      onViewPriceInPointsChange(true)
    }
  }, [viewPriceInPoints, clearViewPriceInPoints, onViewPriceInPointsChange, setSearchBurnEnabled, setPointsToBurn])

  const viewPriceInPointsLabel = intl.formatMessage({
    id: 'Pl7ew0Wylbx4ZUv7DdgF',
    defaultMessage: 'View Price in Points',
    description: 'Checkbox label to show experience search prices as Société points only',
  })

  const viewPriceInPointsItems = useMemo<Array<FilterPanelCheckItem>>(() => [{
    value: 'view-price-in-points',
    checked: viewPriceInPoints,
    label: viewPriceInPointsLabel,
  }], [viewPriceInPoints, viewPriceInPointsLabel])

  return (
    <FilterPanelFilterGroup
      compact={compact}
      loading={loading}
      title={title ?? <LoyaltyTierLabel kind="primary">{getLoyaltyProgramDisplayName()}</LoyaltyTierLabel>}
    >
      {showPointsFilters && (
        <SliderContainer>
          <LoyaltyPointsSliderSearch
            layout="filterPanel"
            cacheKey={cacheKey}
            skipBatchMaxCap={skipBatchMaxCap}
            skipBurnExperiment={skipBurnExperiment}
            onBurnEnabled={clearViewPriceInPoints}
            disabled={viewPriceInPoints}
          />
        </SliderContainer>
      )}
      {compact && showPointsFilters && (
        <FilterPanelCheckboxGroup
          name="viewPriceInPoints"
          items={viewPriceInPointsItems}
          onChange={handleViewPriceInPointsToggle}
        />
      )}
      {!compact && (
        <FilterContent>
          {showPointsFilters && (
            <FilterPanelCheckboxGroup
              name="viewPriceInPoints"
              items={viewPriceInPointsItems}
              onChange={handleViewPriceInPointsToggle}
            />
          )}
          {additionalFilters}
        </FilterContent>
      )}
    </FilterPanelFilterGroup>
  )
}

export default LoyaltySocietePointsFilterGroup
